import { useMemo } from 'react'
import type { ReactElement } from 'react'

import type { ChatFeaturesProps, ChatFeatureValue } from './types/ChatFeaturesTypes'
import type { AssistantChatMessage } from '@/ui/components/ChatMessageTypes/Assistant/types/AssistantMessageTypes'
import { isComponentFunction, isComponentObject } from './types/ChatFeaturesGuards'

import './ChatFeatures.scss'

const createFeatureComponent = (
    value: ChatFeatureValue,
    feature: string,
    index: number,
    message: AssistantChatMessage | undefined
): ReactElement | null => {
    if (typeof value === 'boolean') return null

    let Component
    if (isComponentObject(value)) {
        Component = value.component
    } else if (isComponentFunction(value)) {
        Component = value
    } else {
        Component = undefined
    }

    const attributes = isComponentObject(value) ? value.attributes : undefined

    if (!Component) return null
    if (feature === 'handover' && !message?.assistantHandover) return null

    return (
        <Component
            {...attributes}
            key={index}
            content={message?.content || message?.message}
            messageId={message?.messageId}
        />
    )
}

const ChatFeatures = ({ features, message }: ChatFeaturesProps) => {
    const messageFeatures = useMemo(() => {
        if (!features) return undefined

        const featureComponents: ReactElement[] = []
        let index = 0

        for (const feature of Object.keys(features)) {
            const value: ChatFeatureValue = features[feature]
            const component = createFeatureComponent(value, feature, index, message)
            if (component) featureComponents.push(component)
            ++index
        }

        return featureComponents.length ? featureComponents : undefined
    }, [features, message])

    if (!messageFeatures) return null

    return (
        <div className='chat-features'>
            {messageFeatures}
        </div>
    )
}

export default ChatFeatures
